import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, type Me } from './api';
import { noteStudentPending } from './lib/studentWelcome';

type AuthState = {
  me: Me | null;
  loading: boolean;
  refetch: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  me: null,
  loading: true,
  refetch: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = async () => {
    try {
      const data = await api.get<Me>('/api/auth/me');
      setMe(data);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post('/api/auth/logout');
    setMe(null);
  };

  useEffect(() => { refetch(); }, []);

  useEffect(() => {
    if (me?.id && me.role === 'student' && me.approvalStatus === 'pending') {
      noteStudentPending(me.id);
    }
  }, [me?.id, me?.role, me?.approvalStatus]);

  return (
    <AuthContext.Provider value={{ me, loading, refetch, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

const chosenPlanKey = 'physioedge.chosenPlanId';

export function rememberChosenPlan(planId: string) {
  try { sessionStorage.setItem(chosenPlanKey, planId); } catch { /* ignore */ }
}

export function takeChosenPlan(): string | null {
  try {
    const id = sessionStorage.getItem(chosenPlanKey);
    if (id) sessionStorage.removeItem(chosenPlanKey);
    return id;
  } catch {
    return null;
  }
}

/** Where to send someone after they sign in. Students land in the club. */
export function signedInHome(user: Pick<Me, 'role' | 'needsClinicOnboarding' | 'mustChangePassword'>): string {
  if (user.role === 'admin') return '/admin';
  if (user.role === 'clinic_staff') {
    if (user.needsClinicOnboarding || user.mustChangePassword) return '/clinic/onboarding';
    return '/clinic';
  }
  if (user.role === 'doctor') return '/doctor';
  const chosen = takeChosenPlan();
  if (chosen) return `/student/checkout/${chosen}`;
  return '/student';
}

const pendingApprovalSeenKey = (userId: string) => `physioedge.pendingApprovalSeen:${userId}`;

export function resetPendingApprovalPrompt(userId: string) {
  try { sessionStorage.removeItem(pendingApprovalSeenKey(userId)); } catch { /* ignore */ }
}

export function pendingApprovalPromptIsOpen(userId: string) {
  try { return sessionStorage.getItem(pendingApprovalSeenKey(userId)) !== '1'; } catch { return true; }
}

export function dismissPendingApprovalPrompt(userId: string) {
  try { sessionStorage.setItem(pendingApprovalSeenKey(userId), '1'); } catch { /* ignore */ }
}

/** CTA destination: guests → register, anyone logged in → plans. */
export function startHref(me: Me | null): string {
  return me ? '/plans' : '/register';
}

/** Plan CTA: student → checkout, other logged-in → plans, guest → register. */
export function planHref(me: Me | null, planId: string): string {
  if (me?.role === 'student') return `/student/checkout/${planId}`;
  if (me) return '/plans';
  return '/register';
}
